import React from 'react';
import { Card, CardContent, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import '../Waste/Waste.css';

const data_en = [{
  title: 'Waste',
  description: "Waste management is strongly related to CE; materials are lost daily due to inefficient management or an outdated view of urban waste management. The waste sector is one of the first to be mentioned and has been present in different definitions and works on urban circularity. The sector can help close material cycles, and for this reason, waste was a prominent sector in all the CE plans studied. This sector will also consider the cycles of special materials, such as textiles, electronics, etc.",
  strategies: [
    "Reducing waste generation.",
    "Encouraging recycling.",
    "Separating organic waste from other waste.",
    "Separate collection of special waste (textiles, electronics, etc.).",
    "Waste is treated as resources and energy use of waste."
  ]
},
  {
    title: "Agriculture and Food",
    description: "Food supply is fundamental to maintaining urban functioning. However, a large amount of food is wasted every day in cities. This wasted material can be used within the urban area, mainly to close biological cycles. In this sense, agriculture stands out in urban circularity, as it can recover nutrients from food and produce new food to supply cities. In addition, local food production reduces emissions from transportation products grown in other regions.",
    strategies: [
      "Community vegetable garden or orchard.",
      "Use of food waste to produce compost (home compost bins/neighborhoods/gardens) or methanization.",
      "Apps that sell products cheaper because they are close to expiration.",
      "Apps to buy vegetables from local producers.",
      "Fairs for local organic producers.",
      "Green roofs with vegetable gardens."
    ]
  },
  {
    title: "Local Production and Industry",
    description: "Urban areas have small-scale local production (printers, bakeries, etc.), while some cities are industrialized and have industrial regions. Depending on the city, the characteristics and sectors of production vary. However, whatever the characteristics of local production are, they must be considered when thinking about circularity since they can generate waste, emit gases, and consume the city's natural resources.",
    strategies: [
      "Industrial symbiosis parks.",
      "Using waste generated locally to produce new products locally.",
      "Thinking about future local production according to the city's natural capacity."
    ]
  },
  {
    title: "Energy",
    description: "Every urban area depends on energy to function, but energy generation and consumption do not always renewable. A circular urban area must seek to generate and consume energy locally, cleanly, and sustainably.",
    strategies: [
      "Solar energy generation (roofs generating solar energy).",
      "Energy recovery (waste from the urban area itself).",
      "Reduced energy consumption (buildings that make better use of sunlight)."
    ]
  },
  {
    title: "Mobility and Transport",
    description: "Mobility and transportation are relevant to urban circularity since the sector is responsible for the emission of gases that cause climate change. It also enables people and goods to move around, giving them access to different parts of the city. Therefore, rethinking how mobility and transportation operate and promoting CE in this sector is fundamental for urban circularity.",
    strategies: [
      "Sharing means of transportation (bicycles, cars, motorcycles).",
      "Load sharing.",
      "Encouraging the use of urban transport.",
      "Mobility as a service.",
      "Encouraging the use of electric cars."
    ]
  },
  {
    title: "Water and Sanitation",
    description: "Water is a fundamental resource for the urban ecosystem. The way it is captured from nature, used and treated must be thought of circularly. Water losses during these stages are frequent problems reported by cities. The lack of sewage treatment is also recurrent, especially in developing countries. Ensuring water is used cyclically is fundamental to maintaining this valuable resource, which is often scarce. The regeneration of water bodies is also essential when thinking about circularity on an urban scale.",
    strategies: [
      "Use of rainwater.",
      "Reuse of wastewater.",
      "Development of effective sewage collection and treatment systems.",
      "Reduced water consumption.",
      "Reduction of water losses."
    ]
  },
  {
    title: "Construction and Infrastructure",
    description: "This sector refers to urban areas' physical urban structure (houses, buildings, roads, bridges, etc.) and the construction industry. Construction generates a large amount of waste and has the potential to implement circular initiatives. A city's physical structure needs to be planned for long-term adaptation and used in a circular way to avoid wasting urban space and natural resources.",
    strategies: [
      "Modular construction.",
      "New purposes for urban structures.",
      "Find solutions and new use for construction waste.",
      "New, more sustainable design for homes and buildings."
    ]
  }



];


const data_pt = [
    {
      title: "Resíduos",
      description: "A questão dos resíduos é frequentemente relacionada à EC, materiais são diariamente perdidos por uma má gestão ou por uma visão antiga sobre os resíduos urbanos. O setor resíduo é um dos primeiros a ser citado e esteve presente em diferentes definições e trabalhos sobre circularidade urbana. O setor é capaz de auxiliar no fechamento dos ciclos de diversos materiais e, por esta razão, resíduos foram setores de destaque em todos os Planos de EC estudados. Neste setor, também serão considerados os ciclos de materiais especiais como têxteis, eletrônicos etc.",
      strategies: [
        "Redução da geração de resíduos.",
        "Incentivo à reciclagem.",
        "Separação de resíduos orgânicos dos demais resíduos.",
        "Coleta separada de resíduos especiais (têxteis, eletrônicos etc).",
        "Resíduos tratados como recursos e aproveitamento energético dos resíduos."
      ]
    },
    {
      title: "Agricultura e Alimentos",
      description: "O fornecimento de alimentos é fundamental para manter o metabolismo urbano. No entanto, grande quantidade de alimentos é desperdiçada diariamente nas cidades. Este material desperdiçado pode ser usado dentro da área urbana, principalmente para fechar os ciclos biológicos. Neste sentido, a agricultura se destaca na circularidade urbana, visto que é capaz de recuperar os nutrientes dos alimentos e produzir novos alimentos para abastecer as cidades. Além disso, a produção de alimentos local reduz as emissões provenientes do transporte de produtos cultivados em outras regiões.",
      strategies: [
        "Hortas comunitárias.",
        "Utilização de resíduos alimentares para produção de adubo (composteiras caseiras/bairros/hortas) ou metanização.",
        "Aplicativos que vendem produtos mais baratos por estarem perto do vencimento.",
        "Aplicativos para comprar horticultura dos produtores locais.",
        "Feiras de produtores orgânicos locais.",
        "Telhados verdes."
      ]
    },
    {
      title: "Produção Local e Indústria",
      description: "Áreas urbanas apresentam sua produção local de pequena escala (gráficas, padarias etc.), algumas cidades são industrializadas, possuindo regiões industriais. Dependendo da cidade, as características e os setores de produção variam. No entanto, seja qual for a característica da produção local, esta deve ser considerada ao se pensar a circularidade, visto que podem gerar resíduos, emitir gases e consumir recursos naturais da cidade.",
      strategies: [
        "Parques de simbiose industrial.",
        "Aproveitamento de resíduos para produção de novos produtos.",
        "Projetar a produção futura conforme a capacidade natural da cidade."
      ]
    },
    {
      title: "Energia",
      description: "Toda área urbana depende de energia para manter seu funcionamento, no entanto, nem sempre a geração e o consumo de energia evitam a degradação dos recursos naturais. Uma área urbana circular deve buscar gerar localmente e consumir energia de forma limpa e sustentável.",
      strategies: [
        "Geração de energia solar (telhados gerando energia solar).",
        "Recuperação de energia (resíduos da própria área urbana).",
        "Redução do consumo de energia (prédios com melhor aproveitamento da luz solar)."
      ]
    },
    {
      title: "Mobilidade e Transporte",
      description: "A mobilidade e o transporte são relevantes para a circularidade urbana, visto que o setor é responsável pela emissão de gases causadores das mudanças climáticas. Além disso, este setor também possibilita que pessoas e mercadorias se desloquem, dando acesso aos diferentes locais da cidade. Assim, repensar a maneira com que a mobilidade e o transporte operam e promover a EC neste setor é fundamental para a circularidade urbana.",
      strategies: [
        "Compartilhamento de meios de transporte (bicicleta, carros, motocicletas).",
        "Carga compartilhada.",
        "Incentivo ao uso de transporte urbano.",
        "Mobilidade como serviço.",
        "Incentivo ao uso de carros elétricos."
      ]
    },
    {
        "title": "Água e Saneamento",
        "description": "A água é um recurso fundamental para o ecossistema urbano. O modo como ela é capturada da natureza, utilizada e tratada deve ser pensado de maneira circular. As perdas de água durante estas etapas são problemas frequentes relatados por cidades. A falta de tratamento de esgoto também é recorrente, principalmente em países em desenvolvimento. Garantir que a água seja usada de forma cíclica é fundamental para manter este recurso tão valioso e, que, muitas vezes, é tão escasso. A regeneração de corpos hídricos também é essencial quando se pensa em circularidade em escala urbana.",
        "strategies": [
          "Utilização de água da chuva.",
          "Reutilização de águas residuárias.",
          "Desenvolvimento de sistemas eficazes de coleta e tratamento de esgotos.",
          "Redução do consumo de água.",
          "Redução deperdas de água."
        ]
      },
    {
    title: "Construção e Infraestrutura",
    description: "Este setor se refere à estrutura física urbana (casas, prédios, estradas, pontes etc.) das áreas urbanas e à indústria de construção. A construção gera grande quantidade de resíduos e tem potencial para implementar iniciativas de economia circular. A estrutura física de uma cidade deve ser pensada a longo prazo e utilizada de forma circular para evitar desperdício de espaço urbano e recursos naturais.",
    strategies: [
    "Construção modular.",
    "Novas finalidades para estruturas urbanas.",
    "Aproveitamento de resíduos de construção.",
    "Novo design mais sustentáveis de casas e prédios."
    ]
    }
    ]
  
const LeftToRightBlock = () => {
    const {t, i18n} = useTranslation();
    const linguagem = i18n.language;

    const data = linguagem === 'en' ? data_en : data_pt;


  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <h3>{t('storesKey')}</h3>
      {data.map((item, index) => (
        <Card key={index} style={{ margin: '5px', height: 'auto', minWidth: '250px', maxWidth: 'auto', width: '900px', border: '2px solid #07526a', borderRadius: '8px' }}>
          <CardContent>
            <div style={{ marginTop: '10px', marginBottom: '10px', lineHeight: '1.6', textAlign:'center' }}>
              <Typography variant="h8" className='title-keys' gutterBottom>
              <h3>{item.title}</h3>
              </Typography>
            </div>
            <div style={{ marginBottom: '15px', lineHeight: '1.6'}}>
              <Typography variant="h9" gutterBottom>
                {item.description}
              </Typography>
            </div>
            <h4 >{t('example-sector')}</h4>
            <ul>
              {item.strategies.map((strategy, index) => (
                <li key={index}>{strategy}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default LeftToRightBlock;
